import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GlassDonutChart } from "@/components/charts/GlassDonutChart";
import {
  AXIS_TICK,
  AXIS_TICK_BOLD,
  BAR_ANIMATION,
  ChartTooltip,
  GRID_STROKE,
} from "@/components/charts/chart-kit";
import { MetricCard } from "@/components/ui/Card";
import {
  getBusinessesByType,
  getBusinessOverview,
  getConnectedServiceStatusChart,
} from "@/lib/business-analytics";
import { BRAND, CHART_COLORS } from "@/lib/brand-colors";
import { formatNumber } from "@/lib/utils";
import type { Business, ConnectedService, Customer } from "@/types/database";

type Props = {
  businesses: Business[];
  services: ConnectedService[];
  customers: Customer[];
};

export function BusinessAnalytics({ businesses, services, customers }: Props) {
  const overview = getBusinessOverview(businesses, services, customers);
  const byType = getBusinessesByType(businesses);
  const byStatus = getConnectedServiceStatusChart(services);

  return (
    <div className="space-y-5">
      <div className="stagger-children grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Businesses" value={formatNumber(overview.totalBusinesses)} />
        <MetricCard
          label="Pending approvals"
          value={formatNumber(overview.pendingApprovals)}
          detail={overview.pendingApprovals > 0 ? "Needs review" : "All clear"}
          trend={overview.pendingApprovals > 0 ? "down" : "up"}
        />
        <MetricCard label="Active services" value={formatNumber(overview.activeServices)} />
        <MetricCard
          label="Customers"
          value={formatNumber(overview.totalCustomers)}
          detail={`${formatNumber(overview.shftOriginalCount)} Shft Original`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl bg-canvas p-4 sm:p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">By type</p>
          <p className="mt-0.5 text-xs text-muted">How businesses are distributed across categories</p>
          <div className="mt-4 h-[220px] w-full">
            {byType.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byType} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fill: "#1d1d1f", fontSize: 11, fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => (
                      <ChartTooltip active={active} payload={payload} label={label} valueLabel="businesses" />
                    )}
                    cursor={{ fill: BRAND.accentMuted, radius: 8 }}
                  />
                  <Bar dataKey="value" radius={[6, 10, 10, 6]} maxBarSize={22} {...BAR_ANIMATION}>
                    {byType.map((_, index) => (
                      <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="flex h-full items-center justify-center text-sm text-muted">No businesses yet.</p>
            )}
          </div>
        </div>

        <div className="rounded-3xl bg-canvas p-4 sm:p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Approval status</p>
          <p className="mt-0.5 text-xs text-muted">Connected service applications across the ecosystem</p>
          <div className="mt-4 h-[220px] w-full">
            {byStatus.length > 0 ? (
              <GlassDonutChart data={byStatus} valueLabel="applications" maxSlices={4} minLabelPercent={0.04} />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={[{ name: "None", value: 0 }]} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                    <XAxis dataKey="name" tick={AXIS_TICK_BOLD} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <Bar dataKey="value" fill={BRAND.border} radius={[10, 10, 4, 4]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-sm text-muted">No connected service applications yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
